/**
 * File Utilities
 *
 * Shared utilities for file validation, formatting, and processing.
 * Single source of truth for file-related operations across the app.
 *
 * Core validation utilities are imported from @loader/shared-types.
 * This file provides File-object wrappers for browser use.
 *
 * Used by:
 * - useVersionUpload hook
 * - useCreateTableFlow hook
 * - UploadStep component
 * - VersionUploadZone component
 * - FileDropzone component
 *
 * @module lib/fileUtils
 */

import {
  MAX_UPLOAD_FILE_SIZE as MAX_FILE_SIZE,
  MAX_ERROR_MESSAGE_LENGTH,
  formatFileSize,
  getFileExtension as getExtension,
  isValidFileExtension,
  isValidMimeType,
  ALLOWED_FILE_EXTENSIONS,
  ALLOWED_MIME_TYPES,
} from '@loader/shared-types';

import { isCompressible } from '@/lib/compression';

// Re-export for backwards compatibility
export { formatFileSize, ALLOWED_FILE_EXTENSIONS, ALLOWED_MIME_TYPES };

// ============================================================================
// FILE TYPE CONSTANTS
// ============================================================================

/**
 * Accepted file types string for HTML input accept attribute
 * Uses dotted format required by HTML file inputs
 */
export const ACCEPTED_FILE_TYPES = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');

// ============================================================================
// FILE VALIDATION
// ============================================================================

/**
 * Result of file validation
 */
export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Get file extension from filename (with dot prefix for backwards compatibility)
 *
 * Note: The shared utility returns extensions without dots.
 * This wrapper adds the dot for backwards compatibility with existing code.
 *
 * @param filename - Name of the file
 * @returns Extension including dot (e.g., ".csv")
 */
export function getFileExtension(filename: string): string {
  const ext = getExtension(filename);
  return ext ? `.${ext}` : '';
}

/**
 * Validate file type by checking both MIME type and extension
 * Uses both checks as fallback since browsers may report different MIME types
 *
 * @param file - File to validate
 * @returns True if file type is valid
 *
 * @example
 * validateFileType(csvFile) // true
 * validateFileType(pdfFile) // false
 */
export function validateFileType(file: File): boolean {
  // Check MIME type using shared utility
  const validMimeType = file.type ? isValidMimeType(file.type) : false;

  // Check file extension as fallback using shared utility
  const extension = getExtension(file.name);
  const validExtension = extension ? isValidFileExtension(extension) : false;

  return validMimeType || validExtension;
}

/**
 * Comprehensive file validation
 * Validates both type and size, returning detailed results
 *
 * For compressible files (CSV/JSON), size validation is skipped here
 * because they will be gzip-compressed before upload (typically 5-10x smaller).
 * The actual compressed size is validated in useVersionUpload after compression.
 *
 * @param file - File to validate
 * @param maxSize - Maximum allowed size in bytes (defaults to MAX_FILE_SIZE)
 * @returns Validation result with errors and warnings
 *
 * @example
 * const result = validateFile(file);
 * if (!result.isValid) {
 *   console.error(result.errors);
 * }
 */
export function validateFile(file: File, maxSize: number = MAX_FILE_SIZE): FileValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate file type
  if (!validateFileType(file)) {
    const extensions = ALLOWED_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(', ');
    errors.push(`Invalid file type. Allowed types: ${extensions}`);
  }

  // Validate file size — skip for compressible files (CSV/JSON) since they'll be
  // gzip-compressed before upload and the compressed size is checked in useVersionUpload
  const skipSizeCheck = isCompressible(file.name);

  if (file.size === 0) {
    errors.push('File is empty');
  } else if (!skipSizeCheck && file.size > maxSize) {
    errors.push(`File size (${formatFileSize(file.size)}) exceeds maximum allowed size of ${formatFileSize(maxSize)}`);
  }

  // Add warnings for edge cases (only for non-compressible files)
  if (!skipSizeCheck && file.size > maxSize * 0.9 && file.size <= maxSize) {
    warnings.push('File is close to the maximum size limit');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// ============================================================================
// ERROR HANDLING
// ============================================================================

/**
 * Standard API error response structure
 */
export interface ApiErrorResponse {
  error?: string;
  message?: string;
  code?: string;
}

/**
 * Extract error message from an API error response
 *
 * @param response - Unknown response object
 * @param fallback - Fallback message if extraction fails
 * @returns Error message string
 */
export function extractErrorMessage(response: unknown, fallback: string = 'An error occurred'): string {
  if (!response || typeof response !== 'object') {
    return fallback;
  }

  const error = response as ApiErrorResponse;
  return error.error || error.message || fallback;
}

/**
 * Convert technical error messages to user-friendly messages
 *
 * Maps common technical errors from the API to user-friendly messages
 * that provide clear guidance on how to resolve the issue.
 *
 * @param error - Technical error message from the API
 * @returns User-friendly error message
 *
 * @example
 * ```tsx
 * const friendlyError = getUserFriendlyError('Unsupported file type: application/pdf');
 * // Returns: "This file type is not supported. Please use CSV, JSON, Excel, or Parquet files."
 * ```
 */
export function getUserFriendlyError(error: string | null | undefined): string {
  if (!error) return 'Something went wrong. Please try again.';

  // Map technical errors to user-friendly messages
  const errorLower = error.toLowerCase();

  if (errorLower.includes('unsupported file type')) {
    return 'This file type is not supported. Please use CSV, JSON, Excel, or Parquet files.';
  }
  if (errorLower.includes('no data found')) {
    return 'The file appears to be empty. Please check your file and try again.';
  }
  if (errorLower.includes('storage limit') || errorLower.includes('quota')) {
    return "You've reached your storage limit. Please upgrade your plan or delete some tables.";
  }
  if (errorLower.includes('table limit')) {
    return "You've reached your table limit. Please upgrade your plan or delete some tables.";
  }
  if (errorLower.includes('name') && errorLower.includes('exist')) {
    return 'A table with this name already exists. Please choose a different name.';
  }
  if (errorLower.includes('parse') || errorLower.includes('invalid')) {
    return 'Could not read the file. Please check the file format is correct.';
  }
  if (errorLower.includes('network') || errorLower.includes('connection')) {
    return 'Connection error. Please check your internet and try again.';
  }

  // For other errors, show a simplified version (matches backend sanitizeErrorForClient limit)
  return error.length > MAX_ERROR_MESSAGE_LENGTH ? `${error.substring(0, MAX_ERROR_MESSAGE_LENGTH)}...` : error;
}
